import { MouseEvent, useState } from 'react';
import { useSubscription } from '../contexes/SubscriptionProvider';
import { AnalyticsEvents } from '../enums/AnalyticsEvents';
import { useAnalytics } from '../hooks/useAnalytics';
import { useSnackBar } from '../hooks/useSnackBar';
import { useTranslation } from '../hooks/useTranslation';
import { AgentStatus as AgentStatusInterface } from '../interfaces/Agent';
import { ResponseObject } from '../interfaces/App';

interface UseAgentStatusToggleOptions {
  agentId: string | undefined;
  initialStatus: string;
  updateService: (id: string, data: any) => Promise<ResponseObject | undefined>;
  /** When true, going online without remaining credits opens the upgrade modal. */
  requireSubscription?: boolean;
  /**
   * Call agents need a paid Stripe customer (or enterprise) to provision a
   * phone number. Free-tier companies still have demo credits, so the credits
   * gate alone would hit the API and show a generic failure — prompt upgrade
   * instead.
   */
  requiresPaidPlanForOnline?: boolean;
  onStatusChangeSuccess?: (updatedData: any) => void;
  analyticsSource?: string;
  /** Named in the message telling integrator (MixVoIP) users to route it first. */
  agentName?: string;
}

interface UseAgentStatusToggleReturn {
  agentStatus: string;
  /** True while a status change is in flight; the switch is disabled meanwhile. */
  isUpdating: boolean;
  onStatusClick: (event: MouseEvent<HTMLButtonElement>) => Promise<void>;
  changeStatusModalOpen: boolean;
  setChangeStatusModalOpen: (open: boolean) => void;
}

// Integrator (MixVoIP) agent with no phone number routed to it yet.
const INTEGRATOR_ROUTING_MISSING_REASON = 'integrator_routing_missing';

function isNoSubscriptionError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /no subscription/i.test(message);
}

function companyCanProvisionCallAgentOnline(
  company: {
    enterprise?: boolean;
    billing_customer_id?: string | null;
  } | null
): boolean {
  if (!company) return false;
  if (company.enterprise === true) return true;
  return Boolean(company.billing_customer_id);
}

/**
 * Custom hook to handle agent status toggling logic
 * Works for both call agents and chat agents
 */
const useAgentStatusToggle = ({
  agentId,
  initialStatus,
  updateService,
  requireSubscription = true,
  requiresPaidPlanForOnline = false,
  onStatusChangeSuccess,
  analyticsSource = 'agent_table_page',
  agentName = '',
}: UseAgentStatusToggleOptions): UseAgentStatusToggleReturn => {
  const { t } = useTranslation();
  const displayMessage = useSnackBar();
  const { hasCredits, company } = useSubscription();
  const { track } = useAnalytics();

  const [agentStatus, setAgentStatus] = useState<string>(initialStatus);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const promptUpgrade = (event?: MouseEvent<HTMLButtonElement>) => {
    setChangeStatusModalOpen(true);
    event?.preventDefault();
  };

  const onStatusClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (isUpdating) return;
    if (!agentId) {
      displayMessage(t('Agent ID is missing'), 'error');
      return;
    }

    // Prevent status change if agent is in review
    if (agentStatus === AgentStatusInterface.IN_REVIEW) {
      event.preventDefault();
      displayMessage(
        t('Cannot change status while agent is in review'),
        'warning'
      );
      return;
    }

    const goingOnline = agentStatus === AgentStatusInterface.OFFLINE;

    // Call agents: free / no Stripe customer cannot provision a number.
    if (
      requiresPaidPlanForOnline &&
      goingOnline &&
      !companyCanProvisionCallAgentOnline(company)
    ) {
      promptUpgrade(event);
      return;
    }

    // Gate going online on company credits (capacity), not on the billing-gated
    // Stripe subscription, so members without MANAGE_BILLING can still bring an
    // agent online whenever the company has credits.
    if (requireSubscription && goingOnline && !hasCredits) {
      promptUpgrade(event);
      return;
    }

    setIsUpdating(true);
    try {
      const newStatus = goingOnline
        ? AgentStatusInterface.ONLINE
        : AgentStatusInterface.OFFLINE;

      const updatedAgent = await updateService(agentId, {
        status: newStatus,
      });

      if (updatedAgent?.message?.status) {
        // Track analytics event
        track(AnalyticsEvents.AGENT_STATUS_CHANGED, {
          agentId,
          previousStatus: agentStatus,
          newStatus: updatedAgent.message.status,
          source: analyticsSource,
        });

        setAgentStatus(updatedAgent.message.status);

        // Call optional success callback for additional side effects
        onStatusChangeSuccess?.(updatedAgent.message);

        displayMessage(
          t('Agent status updated to {{status}}', {
            status: updatedAgent.message.status,
          }),
          'success'
        );
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error(`Error changing agent status`, error.message);
      if (goingOnline && isNoSubscriptionError(error)) {
        promptUpgrade();
        return;
      }
      if (error?.reason === INTEGRATOR_ROUTING_MISSING_REASON) {
        displayMessage(
          t(
            'Connect "{{name}}" first: in the VOXBI Cockpit, open Manage agents and send the SIP configuration to this agent, then go to Telephony → Phone numbers, route your number to it and click Save. Then switch it online again.',
            { name: agentName }
          ),
          'error'
        );
        return;
      }
      displayMessage(t('Failed to change agent status'), 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    agentStatus,
    isUpdating,
    onStatusClick,
    changeStatusModalOpen,
    setChangeStatusModalOpen,
  };
};

export default useAgentStatusToggle;
